import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  BookOpen,
  Users,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  MessageSquare,
  FileText,
  BarChart3,
  Settings,
  UserCheck,
  GraduationCap,
  ExternalLink,
  PenSquare,
  LinkIcon,
  UserIcon,
  Space,
  Edit,
} from "lucide-react";
import { useAuth, useNavigation } from "../App";
// import useAuth from "../components/Context/AuthContext";
// import useNavigation from "../components/Context/NavigationContext";
import { useNavigate } from "react-router-dom";
import paths from "../routes";
import {
  useEnhancedProjectsQuery,
  GetEnhancedProjectsParams,
  EnhancedProject,
  getInstructorDashboardData,
  InstructorDashboardData,
} from "../api/projectManagementApi";
import { useQuery } from "@tanstack/react-query";
interface Course {
  id: string;
  name: string;
  code: string;
  students: number;
  assignments: number;
  pendingGrades: number;
  averageGrade: number;
  nextDeadline: string;
  status: "active" | "completed";
}

interface Assignment {
  id: string;
  title: string;
  course: string;
  startDate: string;
  dueDate: string;
  submissions: number;
  totalStudents: number;
  gradedSubmissions: number;
  status: "active" | "overdue" | "grading" | "completed";
}

interface RecentActivity {
  id: string;
  type: "submission" | "grade" | "message" | "announcement";
  description: string;
  time: string;
  course?: string;
}

function CourseCard({
  course,
  assignment,
}: {
  course: EnhancedProject;
  assignment: Assignment;
}) {
  const navigate = useNavigate();
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "overdue":
        return "bg-bits-red-100  text-bits-red-600";
      default:
        return "text-muted-foreground";
    }
  };

  function getDateDifference(
    start_date?: string | null,
    due_date?: string | null
  ) {
    if (!start_date && !due_date) return "Dates not available";

    const now = new Date();
    const start = start_date ? new Date(start_date) : null;
    const due = due_date ? new Date(due_date) : null;

    let diffDays = 0;
    if (due) {
      diffDays = Math.ceil(
        (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
    }

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    let statusString = "On Track";
    if (diffDays < 0) statusString = "Overdue";
    else if (diffDays === 0) statusString = "Due today";
    else if (diffDays === 1) statusString = "1 day left";
    else if (diffDays > 1) statusString = diffDays + " days left";

    const formatDate = (d: Date | null) =>
      d
        ? `${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`
        : "N/A";

    const dateValue =
      start && due
        ? `${formatDate(start)} - ${formatDate(due)}`
        : start
        ? `${formatDate(start)}`
        : due
        ? `${formatDate(due)}`
        : "Dates not available";

    return `${dateValue} (${statusString})`;
  }

  // const submissionRate =
  //   ((course.students - course.pendingGrades) / course.students) * 100;

  // const submissions = `${assignment.submissions} / ${assignment.totalStudents}`;
  // const graded = `${assignment.gradedSubmissions} / ${assignment.submissions}`;
  console.log(course, assignment);
  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow duration-200"
      onClick={() =>
        navigate(
          `${paths.instructorProjectDetails.replace(":projectId", course.id)}`
        )
      }
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex gap-4 items-start justify-between">
            <div>
              <CardTitle
                className="text-lg font-semibold text-bits-grey"
                onClick={() => {
                  navigate(
                    paths.instructorProjectDetails.replace(
                      ":projectId",
                      course.id
                    )
                  );
                }}
              >
                <div className="flex justify-between items-center capitalize">
                  <div>{course?.title}</div>
                  <div
                    className={`ml-4 text-sm px-2 py-1 rounded-md text-center font-medium w-fit
    ${
      course?.status?.toLowerCase().includes("active")
        ? "bg-yellow-100 text-yellow-800"
        : course?.status?.toLowerCase().includes("draft")
        ? "bg-gray-700 text-white"
        : course?.status?.toLowerCase().includes("close")
        ? "bg-red-600 text-white"
        : course?.status?.toLowerCase().includes("deadline")
        ? "bg-red-600 text-white"
        : course?.status?.toLowerCase().includes("archive")
        ? "bg-orange-500 text-white"
        : course?.status?.toLowerCase().includes("complete")
        ? "bg-green-600 text-white"
        : "bg-gray-200 text-gray-800"
    }`}
                  >
                    {course?.status}
                  </div>
                </div>
              </CardTitle>
              <CardDescription>
                <div className="text-sm font-normal text-bits-grey-600">
                  {course?.course?.code}
                </div>
              </CardDescription>
            </div>
            {/* <Badge
              className={getStatusColor(assignment.status)}
              variant="outline"
            >
              {assignment.status}
            </Badge> */}
          </div>
          <div className="flex gap-4 items-start justify-between">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                if (
                  course?.status?.toLowerCase().includes("close") ||
                  course?.status?.toLowerCase() === "closed"
                ) {
                  // Could add a toast here if needed
                  return;
                }
                navigate(
                  `${paths.editProject.replace(
                    ":projectId",
                    course.id
                  )}?mode=edit`
                );
              }}
              disabled={
                course?.status?.toLowerCase().includes("close") ||
                course?.status?.toLowerCase() === "closed"
              }
              className={`border-text-bits-grey-300 z-20 ${
                course?.status?.toLowerCase().includes("close") ||
                course?.status?.toLowerCase() === "closed"
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              variant="outline"
            >
              <Edit className="h-4 w-4" />
            </Button>
            {/* <Button className="border-text-bits-grey-300" variant="outline">
              <LinkIcon className="h-4 w-4" />
            </Button> */}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-2xl font-bold text-bits-blue-neutral-900">
                {(course as any)?.totalStudentCount || 0}
              </div>
              <div className="text-bits-grey-600 font-normal text-sm">
                Total Students
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-bits-blue-neutral-900">
                {course?.submissionCount}
              </div>
              <div className="text-bits-grey-600 font-normal text-sm">
                Submissions
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-bits-blue-neutral-900">
                {(course as any)?.gradedStudentCount || 0}
              </div>

              <div className="text-bits-grey-600 font-normal text-sm">
                Graded
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between text-md font-medium text-bits-grey-600 mb-2">
              <span>Submission Progress</span>
              <span>
                {(course as any)?.totalStudentCount > 0
                  ? `${(
                      ((course?.submissionCount || 0) /
                        (course as any)?.totalStudentCount) *
                      100
                    ).toFixed(0)}%`
                  : "0%"}
              </span>
            </div>
            <Progress
              value={
                (course as any)?.totalStudentCount > 0
                  ? ((course?.submissionCount || 0) /
                      (course as any)?.totalStudentCount) *
                    100
                  : 0
              }
              className="h-2"
            />
          </div>

          <div className="flex items-center gap-20 text-sm">
            <div className="flex items-center space-x-1 justify-between">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-bits-grey-600 text-sm font-semibold">
                {getDateDifference(course?.start_date, course?.due_date)}
              </span>
            </div>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-x-1">
                <UserIcon className="h-4 w-4 text-muted-foreground mt-1" />
                <div className="text-muted-foreground">Your Role:</div>
              </div>
              <div className="text-sm font-semibold ml-1 text-bits-grey-600 capitalize">
                {course?.userRole || "-"}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AssignmentCard({ assignment }: { assignment: Assignment }) {
  const { navigateTo } = useNavigation();
  const navigate = useNavigate();
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "text-bits-blue";
      case "grading":
        return "text-bits-gold";
      case "completed":
        return "text-bits-blue";
      default:
        return "text-muted-foreground";
    }
  };

  const submissionRate =
    (assignment.submissions / assignment.totalStudents) * 100;
  const gradingProgress =
    assignment.submissions > 0
      ? (assignment.gradedSubmissions / assignment.submissions) * 100
      : 0;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow duration-200"
      onClick={() => navigateTo("submissions", { assignmentId: assignment.id })}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{assignment.title}</CardTitle>
            <CardDescription>{assignment.course}</CardDescription>
          </div>
          <Badge
            className={getStatusColor(assignment.status)}
            variant="outline"
          >
            {assignment.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-lg font-bold">
                {assignment.submissions}/{assignment.totalStudents}
              </div>
              <div className="text-muted-foreground">Submissions</div>
            </div>
            <div>
              <div className="text-lg font-bold">
                {assignment.gradedSubmissions}
              </div>
              <div className="text-muted-foreground">Graded</div>
            </div>
          </div>

          <div>
            <div className="flex justify-between text-xs mb-1">
              <span>Submission Rate</span>
              <span>{submissionRate.toFixed(0)}%</span>
            </div>
            <Progress value={submissionRate} className="h-2 mb-2" />

            <div className="flex justify-between text-xs mb-1">
              <span>Grading Progress</span>
              <span>{gradingProgress.toFixed(0)}%</span>
            </div>
            <Progress value={gradingProgress} className="h-2" />
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-1">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                Due: {new Date(assignment.dueDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function InstructorDashboard() {
  const { user, logout } = useAuth();
  const { navigateTo } = useNavigation();
  const navigate = useNavigate();
  const [courses] = useState<Course[]>([]);
  const [assignments] = useState<Assignment[]>([]);
  const [recentActivity] = useState<RecentActivity[]>([]);
  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["instructorDashboard"],
    queryFn: () => getInstructorDashboardData(),
  });

  const totalStudents = courses.reduce(
    (sum, course) => sum + course.students,
    0
  );
  const totalPendingGrades = courses.reduce(
    (sum, course) => sum + course.pendingGrades,
    0
  );
  const activeCourses = courses.filter((c) => c.status === "active").length;
  const averageGrade =
    courses.reduce((sum, course) => sum + course.averageGrade, 0) /
    courses.length;

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "submission":
        return <FileText className="h-4 w-4 text-bits-blue" />;
      case "grade":
        return <CheckCircle className="h-4 w-4 text-bits-gold" />;
      case "message":
        return <MessageSquare className="h-4 w-4 text-bits-gold" />;
      case "announcement":
        return <AlertCircle className="h-4 w-4 text-bits-gold" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };
  const projectQueryParams: GetEnhancedProjectsParams = {
    page: 1,
    limit: 10,
  };

  const {
    data: enhancedProjectsData,
    isLoading: projectsLoading,
    isError: projectsError,
  } = useEnhancedProjectsQuery(projectQueryParams as GetEnhancedProjectsParams);

  function getRecentActivity(recentActivity: RecentActivity[]) {
    var count = 0;
    var summary = "";
    recentActivity.forEach((rec: RecentActivity) => {
      if (rec.type === "grade") {
        count++;
        summary = `${count} Assignments Graded`;
      }
    });
    return summary;
  }

  const getCurrentAssignment = (
    assignments: Assignment[],
    courseName: string
  ): Assignment | undefined => {
    return assignments.find((assignment) => assignment.course === courseName);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <div className="mt-4 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-black">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-muted-foreground font-md mt-1">
            Manage your courses and student progress
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Header actions can be added here if needed */}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3 justify-between">
              <div>
                <p className="text-sm font-normal text-muted-foreground">
                  Total Projects
                </p>
                <div className="text-3xl font-bold text-bits-blue-neutral-900">
                  {dashboardData?.totalProjects}
                </div>
              </div>
              <BookOpen className="h-5 w-5 text-bits-blue" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3 justify-between">
              <div>
                <p className="text-sm font-normal text-muted-foreground">
                  Active Projects
                </p>
                <div className="text-3xl font-bold text-bits-blue-neutral-900">
                  {dashboardData?.activeProjects}
                </div>
              </div>
              <BookOpen className="h-5 w-5 text-bits-blue" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3 justify-between">
              <div>
                <p className="text-sm font-normal text-muted-foreground">
                  Total Students
                </p>
                <div className="text-3xl font-bold text-bits-blue-neutral-900">
                  {dashboardData?.totalStudents}
                </div>
              </div>
              <BookOpen className="h-5 w-5 text-bits-blue" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3 justify-between">
              <div>
                <p className="text-sm font-normal text-muted-foreground">
                  Pending Grades
                </p>
                <div className="text-3xl font-bold text-bits-blue-neutral-900">
                  {dashboardData?.pendingGrades}
                </div>
              </div>
              <BookOpen className="h-5 w-5 text-bits-blue" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-3 justify-between">
              <div>
                <p className="text-sm font-normal text-muted-foreground">
                  Average Grade
                </p>
                <div className="text-3xl font-bold text-bits-blue-neutral-900">
                  {dashboardData?.averageGrade}
                </div>
              </div>
              <Users className="h-5 w-5 text-bits-blue" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        {/* Courses */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-bits-blue">
              Your Projects
            </h2>
            <Button
              variant="outline"
              onClick={() => navigate(paths.viewAllProjects)}
            >
              <ExternalLink className="h-4 w-4 mr-2 text-bits-grey-700" />
              <div className="text-sm font-semibold text-bits-grey-700">
                View All Project
              </div>
            </Button>
          </div>
          <div className="space-y-4 text-lg font-semibold">
            {enhancedProjectsData?.projects?.slice(0, 3).map((course) => {
              console.log(course);
              return (
                <CourseCard
                  key={course.id}
                  course={course}
                  assignment={
                    assignments.find((a) => a.course === course.course.name)!
                  }
                />
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Recent Activity */}
          {/* <Card onClick={() => navigateTo("recent-activity")}>
            <CardHeader>
              <div className="flex gap-4 items-start">
                <MessageSquare className="h-6 w-6 text-bits-blue " />
                <div className="text-lg font-medium text-bits-grey">
                  <CardTitle>Recent Activity</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-bits-green-success-25 w-full mb-3">
                <div className="flex items-start justify-end pl-2 gap-x-4">
                  <CheckCircle className="h-5 w-5 text-bits-green mt-3" />
                  <div className="items-start justify between">
                    <div className="text-md font-bold pt-2">
                      {getRecentActivity(recentActivity)}
                    </div>
                    <div>
                      <div className="pl-2 pr-2">
                        <div className="items-start justify-between">
                          {recentActivity.map((activity) => (
                            <div
                              key={activity.id}
                              className="flex items-start space-x-3"
                            >
                              <div className="flex-1 min-w-0 ">
                                <p className="text-sm font-normal text-muted-foreground pb-1">
                                  {activity.type === "grade"
                                    ? activity.description
                                    : null}
                                </p>
                              </div>
                              <div className="text-xs font-normal text-muted-foreground pb-1">
                                {activity.type === "grade"
                                  ? activity.time
                                  : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-bits-blue-50 w-full">
                <div className="flex items-start justify-end gap-x-4 pl-2">
                  <CheckCircle className="h-5 w-5 mt-3 text-bits-blue-500" />
                  <div className="items-start justify between">
                    <div className="text-md font-bold pt-2">
                      Project Submitted
                    </div>
                    <div>
                      <div className="pl-2 pr-2">
                        <div className="items-start justify-between">
                          {recentActivity.map((activity) => (
                            <div
                              key={activity.id}
                              className="flex items-start space-x-3"
                            >
                              <div className="flex-1 min-w-0 ">
                                <p className="text-sm font-normal text-muted-foreground pb-1">
                                  {activity.type === "submission"
                                    ? activity.description
                                    : null}
                                </p>
                              </div>
                              <div className="text-xs font-normal text-muted-foreground pb-1">
                                {activity.type === "submission"
                                  ? activity.time
                                  : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card> */}
        </div>
      </div>
    </div>
  );
}
